import React from 'react';
import { Wrench, AlertTriangle, CheckCircle, ShieldCheck, XCircle, Clock, Download, Loader2 } from 'lucide-react';
import { KpiCard } from '../../societies/components/tabs/dashboard/KpiCard';
import { VehicleProfileCards } from './VehicleProfileCards';
import { CostComparisonCards } from './CostComparisonCards';
import { EvolutionChart } from './EvolutionChart';
import { BreakdownAnalysisSection } from './BreakdownAnalysisSection';
import { useAuthStore } from '../../../stores/auth-store';
import type { SavReport, BreakdownAnalysis } from '../types/dashboard.types';

interface AfterSalesSectionProps {
  savReport: SavReport;
  periodLabel?: string;
  breakdownAnalysis?: BreakdownAnalysis;
  onExportSav?: () => void;
  exportingSav?: boolean;
}

export const AfterSalesSection: React.FC<AfterSalesSectionProps> = ({
  savReport,
  periodLabel = '12 derniers mois',
  breakdownAnalysis,
  onExportSav,
  exportingSav = false,
}) => {
  const { hasPermission } = useAuthStore();
  const canViewClaims = hasPermission('dashboard.view_sav_claims');
  const canViewAmounts = hasPermission('dashboard.view_sav_amounts');

  const { kpis, vehicleProfile, costs, monthlyVolume, monthlyCosts } =
    savReport;

  return (
    <div>
      {/* Section title */}
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-orange-500 tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-mb-4 tw-min-h-[56px]">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Wrench className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
          <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Mon activité SAV</span>
        </div>
        {onExportSav && canViewAmounts && (
          <button
            onClick={onExportSav}
            disabled={exportingSav}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-white/20 tw-rounded-md tw-text-sm tw-font-medium tw-normal-case hover:tw-bg-white/30 tw-transition-colors disabled:tw-opacity-50"
          >
            {exportingSav ? <Loader2 size={14} className="tw-animate-spin" /> : <Download size={14} />}
            <span className="tw-hidden sm:tw-inline">Export</span>
          </button>
        )}
      </div>

      {/* KPI Cards SAV — Vue d'ensemble */}
      {canViewClaims && (
        <>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-4">
            <KpiCard
              title="Pannes déclarées"
              value={<>{kpis.totalClaims} <span className="tw-text-sm tw-font-normal tw-text-gray-500">({kpis.savRate}% des contrats)</span></>}
              icon={AlertTriangle}
              color="orange"
            />
            <KpiCard
              title="En cours de traitement"
              value={kpis.inProgressClaims}
              icon={Clock}
              color="orange"
            />
            <KpiCard
              title="Pannes clôturées"
              value={kpis.closedClaims}
              icon={CheckCircle}
              color="orange"
            />
          </div>

          {/* KPI Cards SAV — Détail des clôturées */}
          <p className="tw-text-sm tw-text-gray-500 tw-mb-2 tw-ml-1">Détail des pannes clôturées</p>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
            <KpiCard
              title="Prises en charge"
              value={<>{kpis.coveredClaims} <span className="tw-text-sm tw-font-normal tw-text-gray-500">({kpis.coveredPercent}%)</span></>}
              icon={ShieldCheck}
              color="red"
            />
            <KpiCard
              title="Sans suite"
              value={<>{kpis.dismissedClaims} <span className="tw-text-sm tw-font-normal tw-text-gray-500">({kpis.dismissedPercent}%)</span></>}
              icon={XCircle}
              color="red"
            />
          </div>
        </>
      )}

      {/* Coûts des prises en charge */}
      {canViewAmounts && (
        <>
          <p className="tw-text-sm tw-text-gray-500 tw-mb-2 tw-ml-1">Montants des pannes prises en charge</p>
          <div className="tw-mb-4">
            <CostComparisonCards costs={costs} />
          </div>
        </>
      )}

      {/* Graphiques SAV */}
      <div className={`tw-grid tw-grid-cols-1 ${canViewClaims && canViewAmounts ? 'lg:tw-grid-cols-2' : 'lg:tw-grid-cols-1'} tw-gap-4`}>
        {canViewClaims && (
          <EvolutionChart
            title={`Évolution des pannes (${periodLabel})`}
            data={monthlyVolume}
            series={[
              {
                dataKey: 'claimsCount',
                name: 'Pannes',
                color: '#f97316',
              },
            ]}
          />
        )}
        {canViewAmounts && (
          <EvolutionChart
            title={`Évolution des coûts (${periodLabel})`}
            data={monthlyCosts}
            series={[
              {
                dataKey: 'costsAmount',
                name: 'Montant PEC',
                color: '#ef4444',
                isCurrency: true,
              },
            ]}
          />
        )}
      </div>

      {/* Profil véhicule */}
      {canViewClaims && (
        <div className="tw-mt-4">
          <VehicleProfileCards profile={vehicleProfile} />
        </div>
      )}

      {/* Analyse des pannes (marque/modèle) */}
      {canViewClaims && breakdownAnalysis && (
        <div className="tw-mt-4">
          <BreakdownAnalysisSection analysis={breakdownAnalysis} />
        </div>
      )}
    </div>
  );
};
